import PropTypes from 'prop-types';
import React from 'react';
import { findDOMNode } from 'react-dom';
import { UX2 } from '@wsb/guac-widget-core';
import { assign } from 'lodash';
import Breadcrumb from './Breadcrumb';
import dataAids from '../../constants/dataAids';
import editModeViews from '../../constants/editModeViews';
import Field from '../constants/editable-field-tags';
import {
  CategoryListStore,
  ShopStore,
  ScrollWidgetNotifier,
  ProductActions,
  ProductListStore
} from '@wsb/guac-widget-shared/lib/common/ols-core/shop-bundle';
import {
  OlsConfigStore,
  CartActions,
  CartStore,
  OlsConfigActions,
  RouteHandler,
  NumberFormatter,
  ShopViewConstants,
  ScrollWidgetActions
} from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import { YotpoActions } from '@wsb/guac-widget-shared/lib/common/ols-core/shared-bundle';
import { DigitalWalletStore } from '../utils/ols-core';
import { Translate } from '@wsb/vnext-widget-content-translate';
import ProductUtils from '../utils/ProductUtils';
import PixelTracker from '../utils/PixelTracker';
import {
  CoverImage,
  CartList,
  ProductList,
  PlaceholderProductList,
  ProductDetail,
  Fetching,
  ErrorMessage,
  suspenseWrapper
} from './lazy';
import { OlsShopContext } from './OlsShopContext';
import { getFreeShippingText } from './utils/FreeShipping';
import { fetchShippingMethods } from '../utils/ols-core/PaymentRequestApi';
import * as Metrics from './utils/trackMetric';
import * as MetricEvents from '../../constants/metrics';

class OlsShopContainer extends React.Component {
  constructor() {
    super(...arguments);
    this.state = assign(
      {
        freeShippingText: '',
        isCartLoaded: false,
        shippingMethods: []
      },
      this.getStoreState()
    );
  }

  getContextValue() {
    const { cart } = CartStore.getState();
    const { shippingMethods, freeShippingText: freeShippingTextState } = this.state;
    const {
      currency: { format }
    } = OlsConfigStore.getState();

    const translate = Translate(this.props.staticContent);
    const { renderMode, viewDevice, featureFlags, domainName } = this.props;

    const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
    const siteUrl = `${protocol}//${domainName}`;

    const getLinkProps = (view, params = {}, fullUrl = false) => {
      if (typeof window === 'undefined' || (renderMode !== 'PUBLISH' && !fullUrl)) {
        return null;
      }

      // Some resources (like Yotpo) requires the full url, while normal links don't.
      const href = `${fullUrl ? siteUrl : ''}${RouteHandler.generateUrl(view, params)}`;

      return {
        href
      };
    };

    let freeShippingText = '';

    if (cart && shippingMethods) {
      freeShippingText = getFreeShippingText({
        cart,
        shippingMethods,
        translate,
        currencySymbol: format.symbol
      });

      if (!freeShippingTextState && freeShippingText) {
        this.setState({ freeShippingText });
      }
    }

    return {
      getLinkProps,
      renderMode,
      viewDevice,
      translate,
      featureFlags,
      domainName,
      freeShippingText
    };
  }

  getStoreState() {
    const { currentView, selectedTaxonPermalink, searchKeywords, taxonsWereLoaded } =
      ShopStore.getState();
    const { configWasLoaded, store_status } = OlsConfigStore.getState();

    const { totalCount, fetched: fetchedProducts } = ProductListStore.getState();

    const { fetched: fetchedCategories } = CategoryListStore.getState();

    return {
      currentView,
      selectedTaxonPermalink,
      searchKeywords,
      taxonsWereLoaded,
      configWasLoaded,
      provisioned: OlsConfigStore.isProvisioned(),
      provisionInProgress: OlsConfigStore.isProvisionInProgress(),
      provisionTried: OlsConfigStore.provisionTried(),
      storePageUrl: OlsConfigStore.getStorePageUrl(),
      storeStatus: store_status,
      totalCount,
      fetchedProducts,
      fetchedCategories
    };
  }

  UNSAFE_componentWillMount() {
    const {
      featureFlags,
      layoutDesktopColumns,
      defaultSortOption,
      shopRoute,
      renderMode,
      hasNotCreatedAProduct
    } = this.props;
    ShopStore.addListener('change', this.onStoreChange);
    ScrollWidgetNotifier.addListener('shop', this.scrollWidgetIntoView);
    OlsConfigStore.addListener('change', this.onStoreChange);
    CartStore.addListener('change', this.onCartStoreChange);

    OlsConfigActions.setConfig(this.props);

    const productsPerPage = +layoutDesktopColumns * 5;
    const productConfigData = {
      defaultPerPage: productsPerPage,
      defaultSortOption
    };
    ProductActions.setProductConfig(productConfigData);
    OlsConfigActions.loadConfig();
    OlsConfigActions.toggleProductSorting(!!featureFlags.enableOlsProductSorting);

    DigitalWalletStore.setState({ timeout: featureFlags.olsApplePayTimeout });
    const useStaticData = hasNotCreatedAProduct && renderMode === 'EDIT';

    if (typeof window !== 'undefined' && !useStaticData) {
      const searchQuery = window.location.search;
      const urlParams = {};
      const currentPageRoute = this.props.renderMode === 'PUBLISH' ? shopRoute : null;

      // Scroll widget into view when deeplinking to it (unless olsFocus=false)
      if (this.props.renderMode === 'PUBLISH' && !/olsFocus=false/.test(searchQuery)) {
        this._shouldFocusOls =
          /ols\//.test(window.location.pathname) || /olsPage=/.test(searchQuery);
      }

      urlParams.olsFocus = false;
      RouteHandler.init(
        window.location.pathname,
        searchQuery,
        ShopViewConstants.PRODUCT_LISTING,
        urlParams,
        currentPageRoute
      );
    }
  }

  componentWillUnmount() {
    ShopStore.removeListener('change', this.onStoreChange);
    ScrollWidgetNotifier.removeListener('shop', this.scrollWidgetIntoView);
    OlsConfigStore.removeListener('change', this.onStoreChange);
    CartStore.removeListener('change', this.onCartStoreChange);
  }

  async componentDidMount() {
    const { cart, order } = CartStore.getState();
    const { renderMode, hasNotCreatedAProduct } = this.props;

    if (!cart || (cart && (cart.state !== 'cart' || (cart.state === 'cart' && order === null)))) {
      CartActions.loadCart();
    }

    if (this._shouldFocusOls) {
      this._shouldFocusOls = null;
      setTimeout(ScrollWidgetActions.scrollShopWidget, 0);
    }

    if (renderMode === 'EDIT' && !hasNotCreatedAProduct) {
      ProductUtils.loadProduct();
    }

    const { shippingMethods } = this.state;

    if (!shippingMethods?.length) {
      const availableShippingMethods = await fetchShippingMethods();
      this.setState({ shippingMethods: availableShippingMethods });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { freeShippingBannerEnabled } = OlsConfigStore.getState();
    const { freeShippingText } = this.state;
    const { app_key: appKey } = OlsConfigStore.getState().yotpo_config || {};

    if (appKey) {
      window.setTimeout(() => {
        YotpoActions.loadYotpo();
      }, 0);
    }

    // BEAR-488: New sites start with null websiteId
    if (prevProps.websiteId !== this.props.websiteId) {
      OlsConfigActions.clearError();
      OlsConfigActions.setConfig(this.props);
      OlsConfigActions.loadConfig();
    }

    if (
      freeShippingText &&
      prevState.freeShippingText !== freeShippingText &&
      freeShippingBannerEnabled
    ) {
      Metrics.track(MetricEvents.CART_FREE_SHIPPING_BANNER_IMPRESSION);
    }
  }

  shouldRenderSpinner = () => {
    const { routePath } = ShopStore.getState();
    const { selectedTaxonPermalink, configWasLoaded } = this.state;
    const defaultCategory = OlsConfigStore.getDefaultCategory();
    const validRoute = routePath === RouteHandler.PRODUCTS_ROUTE || routePath === null;
    const showSpinner = validRoute && configWasLoaded && defaultCategory && !selectedTaxonPermalink;
    return showSpinner;
  };

  onStoreChange = () => {
    this.setNumberConfig();
    this.setState(this.getStoreState());

    if (this.props.renderMode === 'PUBLISH' && OlsConfigStore.getState().fb_pixel_tracking) {
      PixelTracker.enable();
    }
  };

  onCartStoreChange = () => {
    const { isCartLoaded, currentView } = this.state;
    const { cart } = CartStore.getState();

    if (currentView === ShopViewConstants.CART) {
      if (!isCartLoaded && cart) {
        this.setState({ isCartLoaded: true });
      }

      this.setState({ freeShippingText: '' });
    }
  };

  scrollWidgetIntoView = () => {
    const { renderMode } = this.props;
    const shopContainerNode = this.shopContainerRef && findDOMNode(this.shopContainerRef);
    const getStickyNavHeight = () => {
      const stickyNav = document.querySelector('[id^="header_stickynav"]');
      if (stickyNav && stickyNav.offsetHeight) {
        return stickyNav.offsetHeight;
      }
      return 0;
    };

    if (shopContainerNode) {
      if (renderMode === 'PUBLISH' && shopContainerNode.scrollIntoView) {
        setTimeout(() => {
          shopContainerNode.scrollIntoView(true);
          document.body.scrollTop -= getStickyNavHeight();
          if (document.documentElement) {
            document.documentElement.scrollTop -= getStickyNavHeight();
          }
        }, 0);
      } else {
        const previewContainer = Array.from(
          document.querySelectorAll('.viewport, #render-container')
        ).find(x => x.contains(shopContainerNode));

        if (previewContainer) {
          setTimeout(() => {
            previewContainer.scrollTop +=
              shopContainerNode.getBoundingClientRect().top -
              previewContainer.getBoundingClientRect().top -
              getStickyNavHeight();
          }, 0);
        }
      }
    }
  };

  setNumberConfig = () => {
    const olsConfigStoreState = OlsConfigStore.getState();
    NumberFormatter.setNumberFormat(olsConfigStoreState.number.format);
    NumberFormatter.setCurrencyFormat(olsConfigStoreState.currency.format);
  };

  getBreadcrumbView = () => {
    const { renderMode, editModeView } = this.props;
    const { currentView } = this.state;

    if (renderMode === 'EDIT' && editModeView === editModeViews.PRODUCT_DETAILS) {
      return ShopViewConstants.PRODUCT_DETAIL;
    }

    if (renderMode === 'EDIT' && editModeView === editModeViews.PRODUCT_LIST) {
      return ShopViewConstants.PRODUCT_LISTING;
    }

    return currentView;
  };

  generateHeading = (title, dataAid) => {
    if (!title) return null;

    const styles = {
      searchTitle: {
        'marginBottom': 'large',
        'marginTop': 'large',
        'textAlign': 'center',
        '@sm': {
          display: 'none'
        }
      }
    };
    return (
      <UX2.Group.Group>
        <UX2.Element.Heading.Product
          data-aid={ dataAid }
          isInternalPage={ this.props.isInternalPage }
          data-field-id={ Field.TITLE }
          order={ this.props.order }
          style={ styles.searchTitle }
        >
          { title }
        </UX2.Element.Heading.Product>
      </UX2.Group.Group>
    );
  };

  render() {
    const { freeShippingBannerEnabled } = OlsConfigStore.getState();
    const {
      currentView,
      selectedTaxonPermalink,
      searchKeywords,
      taxonsWereLoaded,
      provisioned,
      provisionInProgress,
      provisionTried,
      configWasLoaded,
      totalCount,
      fetchedProducts,
      fetchedCategories
    } = this.state;
    const { category, section, sectionTitle, renderMode, hasNotCreatedAProduct, editModeView } =
      this.props;
    const { translate, freeShippingText } = this.getContextValue();
    const { Container, Block, Text } = UX2.Element;
    const { Base } = UX2;
    let ProductComponent, title, categoryTitle, pageTitle, shopHeader;
    let dataAid = dataAids.SHOP_TITLE_RENDERED;
    let categoryDetails = {};
    let showTitle = true;
    let showFreeShippingBanner = false;
    const useStaticData = hasNotCreatedAProduct && renderMode === 'EDIT';
    const extraProps = { useStaticData };
    const hasProducts = !hasNotCreatedAProduct;

    const setDefaultCategoryDetails = () => {
      if (!taxonsWereLoaded || !configWasLoaded || this.shouldRenderSpinner()) {
        title = null;
        return;
      }

      // We want to default to sectionTitle if the next conditionals fail
      title = sectionTitle;

      // If store is soft provisioned but not assigned a proper schema yet, we need to fall back on
      // the section title and bail here, since it's likely that the "all products" title isn't seeded correctly yet.
      if (this.state.storeStatus === 'NEW') return;

      const allProductsInfo = OlsConfigStore.getAllProductsInfo();
      if (allProductsInfo && allProductsInfo.name && !selectedTaxonPermalink) {
        title = null;
        categoryTitle = categoryDetails.name = allProductsInfo.name;
        categoryDetails.image = allProductsInfo.image_url;
      }

      if (selectedTaxonPermalink && taxonsWereLoaded) {
        categoryDetails = CategoryListStore.getTaxonInfo(selectedTaxonPermalink);
        categoryTitle = categoryDetails && categoryDetails.name;
        title = null;
      }
    };

    if (renderMode === 'EDIT') {
      if (editModeView === editModeViews.PRODUCT_DETAILS) {
        showTitle = false;
        ProductComponent = ProductDetail;
      } else {
        ProductComponent = ProductList;
        setDefaultCategoryDetails();
      }
    } else if (['ADD', 'DISPLAY', 'SAMPLE'].indexOf(renderMode) !== -1) {
      ProductComponent = provisioned && hasProducts ? ProductList : PlaceholderProductList;
      if (provisioned && hasProducts) {
        setDefaultCategoryDetails();
      } else {
        title = sectionTitle;
      }
    } else if ((!provisionTried && (!provisioned || provisionInProgress)) || !configWasLoaded) {
      extraProps.fetching = true;
      extraProps.pageTransition = true;
      ProductComponent = Fetching;
    } else if (provisionTried && !provisioned && !useStaticData) {
      extraProps.message = translate('products.error_fetching_product_data');
      ProductComponent = ErrorMessage;
    } else if (currentView === ShopViewConstants.PRODUCT_DETAIL) {
      showTitle = false;
      ProductComponent = ProductDetail;
    } else if (currentView === ShopViewConstants.CART) {
      ProductComponent = CartList;
      dataAid = dataAids.CART_TITLE_RENDERED;
      title = translate('cart.cart');
      showTitle = true;
      showFreeShippingBanner = freeShippingText && freeShippingBannerEnabled;
    } else {
      ProductComponent = ProductList;
      if (currentView === ShopViewConstants.SEARCH_RESULTS) {
        let resultTitleString = 'no_search_results';
        if (totalCount > 0) {
          resultTitleString = 'search_results_page_title';
        } else {
          dataAid = dataAids.SEARCH_NO_RESULTS_MESSAGE_RENDERED;
        }
        title = translate(`products.${resultTitleString}`, {
          query: searchKeywords,
          count: totalCount
        });
      } else {
        setDefaultCategoryDetails();
      }
    }

    const showCoverImage =
      OlsConfigStore.getState().taxon_show_extended_details &&
      categoryTitle &&
      categoryDetails.image;

    const styles = {
      shopHeader: {
        'textAlign': 'center',
        'marginBottom': 'small',
        '@sm': {
          textAlign: 'left'
        }
      },
      content: {
        position: 'relative',
        minHeight: '250px'
      },
      freeShippingBanner: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60px',
        padding: '0 !important', // override default padding because website styles change it dynamically
        backgroundColor: 'primary'
      }
    };

    if (!showCoverImage) {
      if (showTitle) {
        pageTitle = this.generateHeading(title, dataAid);
      } else {
        pageTitle = null;
      }

      shopHeader = (
        <Block style={ styles.shopHeader }>
          <Breadcrumb currentView={ this.getBreadcrumbView() } editMode={ renderMode === 'EDIT' } />
        </Block>
      );
    }

    const sectionStyles = {
      font: 'alt',
      position: 'relative'
    };

    const sectionProps = {
      category: category || 'neutral',
      section: section || 'default'
    };

    const { Section } = UX2.Group;
    return (
      <OlsShopContext.Provider value={ this.getContextValue() }>
        { showFreeShippingBanner ? (
          <Section style={ styles.freeShippingBanner } category='primary'>
            <Base section='overlay'>
              <Text>{ freeShippingText }</Text>
            </Base>
          </Section>
        ) : null }
        <Block
          ref={ ref => {
            this.shopContainerRef = ref;
          } }
          style={{
            minHeight:
              typeof window === 'undefined' || !fetchedProducts || !fetchedCategories
                ? '100vh'
                : 'unset'
          }}
        >
          { showCoverImage && suspenseWrapper(<CoverImage categoryDetails={ categoryDetails } />) }
          <Section style={ sectionStyles } { ...sectionProps }>
            <Container category={ category } section={ section } data-container-id='ols-shop'>
              <Block
                ref={ ref => {
                  this.shopContainerRef = ref;
                } }
              >
                { shopHeader }
                { pageTitle }
                <Block style={ styles.content }>
                  { suspenseWrapper(
                    <ProductComponent
                      { ...this.props }
                      { ...extraProps }
                      currentView={ currentView }
                      categoryDetails={ categoryDetails }
                    />
                  ) }
                </Block>
              </Block>
            </Container>
          </Section>
        </Block>
      </OlsShopContext.Provider>
    );
  }
}

OlsShopContainer.propTypes = {
  imageCropMethod: PropTypes.string,
  renderMode: PropTypes.string,
  viewDevice: PropTypes.string,
  sectionTitle: PropTypes.string,
  shopWidgetPageId: PropTypes.string,
  shopWidgetRoute: PropTypes.string,
  translate: PropTypes.func.isRequired,
  featureFlags: PropTypes.object,
  staticContent: PropTypes.object,
  domainName: PropTypes.string,
  layoutDesktopColumns: PropTypes.number,
  layoutMobileColumns: PropTypes.number,
  defaultSortOption: PropTypes.any,
  olsStatus: PropTypes.string,
  category: PropTypes.string,
  isInternalPage: PropTypes.bool,
  order: PropTypes.any,
  section: PropTypes.string,
  hasNotCreatedAProduct: PropTypes.bool,
  editModeView: PropTypes.string,
  isReseller: PropTypes.bool,
  shopRoute: PropTypes.string,
  websiteId: PropTypes.string,
  galleryShowBorder: PropTypes.bool
};

export default OlsShopContainer;
